import { auth } from "@lumberset/api-client";
import { Button, Heading, Logo } from "@lumberset/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/accept-invitation/$id")({
  beforeLoad: async ({ location }) => {
    const { data } = await auth.getSession();
    // The invited person signs in or creates their account first, then lands back here.
    if (!data) throw redirect({ to: "/login", search: { redirect: location.href } });
  },
  component: AcceptInvitation,
});

function AcceptInvitation() {
  const { id } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();

  const accept = useMutation({
    mutationFn: async () => {
      const result = await auth.organization.acceptInvitation({ invitationId: id });
      if (result.error)
        throw new Error(result.error.message ?? "This invitation can no longer be used.");
    },
    onSuccess: async () => {
      // Accepting makes the new workspace the one this session works in, so nothing
      // remembered from another workspace may show on the next screen.
      queryClient.clear();
      await router.invalidate();
      router.history.push("/");
    },
  });

  return (
    <div className="pt-safe pb-safe flex min-h-dvh flex-col items-center justify-center bg-ui-bg-subtle px-4 py-10">
      <Logo className="mb-8 text-[17px]" markClassName="size-7" />
      <div className="flex w-full max-w-[400px] flex-col gap-y-5 rounded-xl bg-ui-bg-base p-6 shadow-elevation-card-rest sm:p-8">
        <div>
          <Heading>You have been invited to join a team</Heading>
          <p className="txt-small mt-1 text-ui-fg-subtle">
            Accept to work on its projects and takeoffs. Make sure you are signed in with the email
            the invitation was sent to.
          </p>
        </div>
        {accept.error ? <p className="txt-small text-ui-fg-error">{accept.error.message}</p> : null}
        <Button size="large" block isLoading={accept.isPending} onClick={() => accept.mutate()}>
          Accept invitation
        </Button>
        {/* Sign-in pages send a signed-in person home, so sign out first, then come back here. */}
        <button
          type="button"
          className="txt-small text-center text-ui-fg-interactive"
          onClick={async () => {
            await auth.signOut();
            queryClient.clear();
            await router.navigate({
              to: "/login",
              search: { redirect: `/accept-invitation/${id}` },
            });
          }}
        >
          Use a different account
        </button>
      </div>
    </div>
  );
}
